import { Col, Row } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import Form from "../../form-components/from";
import { INITIAL_PAGE } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import { getUserStatus } from "../../../models/user/enum";
import { getStatusDeliveryWorker } from "../../../models/delivery-workers/enum";
import EndPoints from "../../../services/end-points";
import DeliveryWorkerContext from "../../../context/delivery-worker/context";
import { addSearchToURL, filtersToQueryString, getFiltersFromURL, getKeywordFromURL } from "../../../utils/helpers/url";
import { useLocation } from "react-router-dom";
import { ICity } from "../../../models/city/response";
import { IStore } from "../../../models/store/response";
import { IDriverClassification } from "../../../models/driver-classification/response";

interface IProps { }

const DeliveryWorkerFilter: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { actions, query, stores, storesLoading } = useContext(DeliveryWorkerContext);

  const statuses = [1, 2];
  const [cities, setCities] = useState<ICity[]>([]);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [driverClassifications, setDriverClassifications] = useState<IDriverClassification[]>([]);
  const [driverClassificationsLoading, setDriverClassificationsLoading] = useState<boolean>(false);
  const { direction } = useContext(AppContext);
  const location = useLocation();
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});

  useEffect(() => {
    const filters = getFiltersFromURL();

    const initialValuesObj = {};
    filters.forEach((f) => {
      initialValuesObj[f.name] = f.value;
    });

    setInitialValues(initialValuesObj);
  }, [location.search]);

  useEffect(() => {
    const getCities = async () => {
      try {
        const { data } = await EndPoints.city.getAllCities({
          light: true,
          perPage: -1,
        });
        setCities(data.data);
        setCitiesLoading(false);
      } catch (error) {
      } finally {
        setCitiesLoading(false);
      }
    };
    getCities();
  }, [direction]);

  useEffect(() => {
    const getDriverClassifications = async () => {
      try {
        setDriverClassificationsLoading(true);
        const { data } = await EndPoints.driverClassification.getAllDriverClassifications({
          perPage: -1,
        });
        setDriverClassifications(data.data);
      } catch (error) {
      } finally {
        setDriverClassificationsLoading(false);
      }
    };
    getDriverClassifications();
  }, [direction]);
  return (
    <Form
      formId="delivery-worker-filter"
      defaultValues={initialValues}
      onSubmit={(data) => {
        const keyword = getKeywordFromURL();
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: keyword,
          filters: [
            data?.status
              ? {
                name: "status",
                value: data.status,
                operation: "eq",
              }
              : undefined,
            data?.statusWorker
              ? {
                name: "statusWorker",
                value: data.statusWorker,
                operation: "eq",
              }
              : undefined,
            data?.cityId
              ? {
                name: "cityId",
                value: data.cityId,
                operation: "eq",
              }
              : undefined,
            data?.storeId
              ? {
                name: "storeId",
                value: data.storeId,
                operation: "eq",
              }
              : undefined,
            data?.driverClassificationId
              ? {
                name: "driverClassificationId",
                value: data.driverClassificationId,
                operation: "eq",
              }
              : undefined,
          ].filter((f) => f),
        };
        const queryString = filtersToQueryString(filter.filters);
        window.history.replaceState(null, "", `?${queryString}`);
        addSearchToURL(keyword);
        actions.setQuery(filter);
      }}
    >
      <Row justify={"start"} gutter={[10, 0]}>
        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="status"
            label={`${t("status")}`}
            width="large"
            input={{
              type: "select",
              options: statuses.map((status, index) => {
                return {
                  label: t(getUserStatus(status) as string),
                  value: status as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="statusWorker"
            label={`${t("current_status")}`}
            width="large"
            input={{
              type: "select",
              options: [1, 2, 3].map((status, index) => {
                return {
                  label: t(getStatusDeliveryWorker(status) as string),
                  value: status as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="cityId"
            label={`${t("city")}`}
            width="large"
            input={{
              type: "select",
              loading: citiesLoading,
              allowSearch: true,
              options: cities.map((city, index) => {
                return {
                  type: "select",
                  label: city?.name as string,
                  value: city?.id as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="storeId"
            label={`${t("store")}`}
            width="large"
            input={{
              type: "select",
              loading: storesLoading,
              allowSearch: true,
              options: stores.map((store: IStore) => ({
                label: store?.name as string,
                value: store?.id as number,
              })),
            }}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="driverClassificationId"
            label={`${t("driver_classification")}`}
            width="large"
            input={{
              type: "select",
              loading: driverClassificationsLoading,
              allowSearch: true,
              options: driverClassifications.map((classification) => ({
                label: classification?.name as string,
                value: classification?.id as number,
              })),
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default DeliveryWorkerFilter;
